import Button from 'v3/common/components/Button';
import {
  FUND_PLATFORMS,
  PaymentUIConfig,
  PAYTMENT_STATUS,
  UseAlgoversePaymentReturnType,
} from './types.type';
import Lottie from 'lottie-react';
import Loading from './Lottie/Loading.json';
import Success from './Lottie/Success.json';
import classNames from 'classnames';
import { PlansAlgoverseProps } from '../type';

export const getPaymentUIConfig = (
  deps: UseAlgoversePaymentReturnType & { selectedPlan: PlansAlgoverseProps['activePlan'] | null }
): Record<PAYTMENT_STATUS, PaymentUIConfig> => {
  const {
    paymentStatus,
    setPaymentStatus,
    selectedPlan,
    marginResponse,
    algoversePaymentResponse,
    selectedFoundPlatform,
    setSelectedFoundPlatform,
    handlePayment,
    setShowModal,
    countdown,
    orderId,
  } = deps;

  const walletBalance =
    marginResponse?.status === 'success'
      ? `₹${Number(marginResponse.data.equity.available_margin).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 0;

  return {
    [PAYTMENT_STATUS.SUCCESS]: {
      title: 'Payment successful',
      renderBody: () => <Lottie animationData={Success} loop className="success-lottie" />,
      renderFooter: () => (
        <Button className="clickable-btn" onClick={() => (window.location.href = '/dashboard')}>
          Redirect to Algoverse ({countdown.success})
        </Button>
      ),
    },

    [PAYTMENT_STATUS.PENDING]: {
      title: 'Payment processing',
      renderBody: () => (
        <>
          <Lottie animationData={Loading} loop className="loading-lottie" />
          <p className="pending-countdown-text">Waiting for payment confirmation ({countdown.pending}s)</p>
        </>
      ),
      renderFooter: () => (
        <Button className="clickable-btn" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
      ),
    },

    [PAYTMENT_STATUS.FAILED]: {
      title: 'Something went wrong!',
      renderBody: () => {
        let errorMessage = 'Something went wrong';
        let errorCode = 'Unhandled API error';

        if (algoversePaymentResponse?.status === 'error') {
          errorMessage = algoversePaymentResponse.errors?.[0].message;
          errorCode = algoversePaymentResponse.errors?.[0].error_code;
        } else if (algoversePaymentResponse?.status === 'success') {
          errorMessage = algoversePaymentResponse.data.error_reason ?? errorMessage;
        }
        return (
          <div className="content">
            <>
              <div className="d-flex justify-content-center align-items-center flex-column">
                <>
                  <h6>Error code: {errorCode}</h6>
                  <h6>Reference Id: {orderId}</h6>
                  <p>{errorMessage}</p>
                </>
              </div>
            </>
          </div>
        );
      },
      renderFooter: () => (
        <div className="d-flex justify-content-center align-items-center gap-5 w-100">
          <div className="w-100">
            <Button className="clickable-btn" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
          </div>
          <div className="w-100">
            <Button onClick={handlePayment}>Try again</Button>
          </div>
        </div>
      ),
    },

    [PAYTMENT_STATUS.INSUFFICIENT_FUNDS]: {
      title: 'Buying backtesting plans',
      renderBody: () => (
        <>
          <div className="content">
            <div
              className={classNames('highlight-card', {
                'insufficient-found-card': paymentStatus === PAYTMENT_STATUS.INSUFFICIENT_FUNDS,
              })}
            >
              <div className="highlight-card__left">
                <h6>{selectedPlan?.plan_name}</h6>
                <p>{selectedPlan?.historic_run_limit} credits</p>
              </div>

              <div className="highlight-card__right">
                <h6>₹{selectedPlan?.price}</h6>
                <p>GST applicable</p>
              </div>
            </div>
            <p>This Amount will be deducted from your Upstox wallet.</p>
            {paymentStatus === PAYTMENT_STATUS.INSUFFICIENT_FUNDS && (
              <span className="insufficient-found-text">
                Insufficient funds to buy {selectedPlan?.plan_name} plan
              </span>
            )}
          </div>

          <div className="terms-conditions">
            <h6>
              <u>Terms & conditions</u>
            </h6>
            <p>
              Your plan is valid for 30 days from the date of purchase, after which any unused tokens will
              expire.
            </p>
          </div>
        </>
      ),
      renderFooter: () => (
        <>
          <div>
            <p>Wallet balance</p>
            <p>{walletBalance}</p>
          </div>
          <div>
            <Button onClick={() => setPaymentStatus(PAYTMENT_STATUS.ADD_FUNDS)}>Add funds to wallet</Button>
          </div>
        </>
      ),
    },

    [PAYTMENT_STATUS.ADD_FUNDS]: {
      title: 'Payment summary',
      renderBody: () => (
        <div className="payment-summary">
          <div className="payment-summary__card">
            <h6>How to recharge wallet by platform</h6>
            <div className="web-mobile-btn">
              <Button
                className={selectedFoundPlatform == FUND_PLATFORMS.MOBILE ? 'transparent-btn' : ''}
                onClick={() => setSelectedFoundPlatform(FUND_PLATFORMS.WEB)}
              >
                Web
              </Button>
              <Button
                className={selectedFoundPlatform == FUND_PLATFORMS.WEB ? 'transparent-btn' : ''}
                onClick={() => setSelectedFoundPlatform(FUND_PLATFORMS.MOBILE)}
              >
                Mobile
              </Button>
            </div>
          </div>
          <div className="fund-platform-selection">
            {selectedFoundPlatform === FUND_PLATFORMS.WEB && (
              <>
                <div className="fund-platform-content">
                  <div>
                    <h6>Recharge Upstox wallet to continue</h6>
                    <p>Please recharge your Upstox wallet to continue purchasing your plan.</p>
                  </div>
                  <Button
                    className="clickable-btn"
                    onClick={() => window.open('https://pro.upstox.com/funds/securities/wallet', '_blank')}
                  >
                    Go to pro.upstox.com
                  </Button>
                </div>
                <div className="fund-platform-content-img">
                  <img src="/algoverse-wallet.png" className="algoverse-wallet" alt="algoverse-wallet" />
                </div>
              </>
            )}
            {selectedFoundPlatform === FUND_PLATFORMS.MOBILE && (
              <div className="scan-qr-code">
                {/* <Button
                  onClick={() => window.open('https://upstox.onelink.me/0H1s/homepage', '_blank')}
                >Go to Home</Button> */}
                {/* <img src="/algoverse-qr-code.png" className="algoverse-qr-code" alt="algoverse-qr-code" /> */}
                <div>
                  {/* <h6>Scan QR code to login into Pro Mobile</h6> */}
                  <p>Please recharge your Upstox wallet to continue purchasing your plan.</p>
                </div>
              </div>
            )}
          </div>
          <div className="success-highlight-card ">
            <div>
              <h6>Added funds successfully?</h6>
              <p>Return to continue your purchase</p>
            </div>
            <div>
              <Button className="clickable-btn" onClick={() => setPaymentStatus(PAYTMENT_STATUS.INITIALIZED)}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      ),
      renderFooter: () => null,
    },

    [PAYTMENT_STATUS.INITIALIZED]: {
      title: 'Buying backtesting plans',
      renderBody: () => (
        <div className="content">
          <div
            className={classNames('highlight-card', {
              'insufficient-found-card': paymentStatus === PAYTMENT_STATUS.INSUFFICIENT_FUNDS,
            })}
          >
            <div className="highlight-card__left">
              <h6>{selectedPlan?.plan_name}</h6>
              <p>{selectedPlan?.historic_run_limit} credits</p>
            </div>
            <div className="highlight-card__right">
              <h6>₹{selectedPlan?.price}</h6>
              <p>GST applicable</p>
            </div>
          </div>
          <p>This Amount will be deducted from your Upstox wallet.</p>
          {paymentStatus == PAYTMENT_STATUS.INSUFFICIENT_FUNDS && (
            <span className="insufficient-found-text">
              Insufficient funds to buy {selectedPlan?.plan_name} plan
            </span>
          )}
          <div className="mt-4">
            <h6>
              <u>Terms & conditions</u>
            </h6>
            <p>
              Your plan is valid for 30 days from the date of purchase, after which any unused tokens will
              expire.
            </p>
          </div>
        </div>
      ),
      renderFooter: () => (
        <>
          <div>
            <p>Wallet balance</p>
            <p>{walletBalance}</p>
          </div>
          <div>
            <Button onClick={handlePayment}>Buy Now</Button>
          </div>
        </>
      ),
    },
  };
};
